import { Injectable, Injector, inject } from '@angular/core';
import { AnyRecommendee } from '@app/recommendations/recommendations.model';
import { InputContext } from '@app/user-content/user-input-v2/input.model';
import {
  APIParameterModel,
  APIResponseModel,
  AssessmentFormDataModel,
  ViewModel,
} from '@app/user-content/user-input-v2/inputs/assessment/assessment.model';
import { AssessmentMapper } from '@app/user-content/user-input-v2/inputs/assessment/services/assessment-mapper.service';
import { AssessmentService } from '@app/user-content/user-input-v2/inputs/assessment/services/assessment.service';
import { addOwnerDetailsToInput } from '@app/user-content/user-input-v2/utils/content-owner-helper';
import { RenderMode } from '@app/user-content/user-input/user-input.model';
import { UserGroupListService } from '@app/user/services/user-group-list.service';
import {
  AssessContentImage,
  type AssessContentImageValue,
} from '@degreed/lxp/assess/services';
import { InputTypes, LDFlagsService } from '@degreed/lxp/services';
import { AuthService } from '@degreed/lxp/services/authorization';
import {
  CATALOG_INPUT_TYPE,
  CatalogContentApiService,
  type CreateUpdateCatalogAssessmentRequest,
  type GetCatalogAssessmentResponse,
} from '@degreed/lxp/services/content';
import { Observable, map, switchMap } from 'rxjs';

/**
 * `AssessContentImage` (`libs/lxp/features/assess/services`) concrete adapter — bound once, at
 * the app root (`app.module.ts`, next to `AssessContentSkills`). Same localized/legacy split as
 * `LxpAssessContentSkillsAdapter` (see that class's own doc comment for the full 401/session-loss
 * story this mirrors); the cover image lives on the same platform Input row skills do
 * (`imageUrl`/`resourceImageId`), so this adapter reuses the exact same two plumbing paths,
 * replacing the image fields instead of `tags`.
 */
@Injectable()
export class LxpAssessContentImageAdapter extends AssessContentImage {
  private readonly ldFlagsService = inject(LDFlagsService);
  private readonly authService = inject(AuthService);
  private readonly userGroupListService = inject(UserGroupListService);

  private readonly catalogInjector = Injector.create({
    providers: [
      CatalogContentApiService,
      { provide: CATALOG_INPUT_TYPE, useValue: InputTypes.Assessment },
    ],
    parent: inject(Injector),
  });
  private readonly catalogApi = this.catalogInjector.get(
    CatalogContentApiService
  );

  private readonly legacyInjector = Injector.create({
    providers: [AssessmentService, AssessmentMapper],
    parent: inject(Injector),
  });
  private readonly legacyAssessmentService =
    this.legacyInjector.get(AssessmentService);
  private readonly legacyAssessmentMapper =
    this.legacyInjector.get(AssessmentMapper);

  public override load(inputId: number): Observable<AssessContentImageValue> {
    return this.isLocalized()
      ? this.catalogApi.get<GetCatalogAssessmentResponse>(inputId).pipe(
          map((details) => ({
            imageUrl: details.imageUrl ?? null,
            resourceImageId: normalizeResourceImageId(details.resourceImageId),
          }))
        )
      : this.legacyAssessmentService.getAssessment(inputId, true).pipe(
          // The legacy response's own `imageUrl` may be a plain URL or a stringified
          // ResourceImage (same "legacy hydration shape" `content-image-upload-field`'s own
          // ngOnInit already parses); that parse happens once the field mounts with this
          // raw value patched onto its form, so there's no need to duplicate it here.
          map((res) => ({
            imageUrl: res.imageUrl ?? null,
            resourceImageId: null,
          }))
        );
  }

  public override save(
    inputId: number,
    value: AssessContentImageValue
  ): Observable<AssessContentImageValue> {
    return this.isLocalized()
      ? this.saveLocalized(inputId, value)
      : this.saveLegacy(inputId, value);
  }

  /** Same formula as `LxpAssessContentSkillsAdapter.isLocalized` — see that class's own doc
   *  comment for why it's re-derived here rather than imported from the lazy-loaded catalog
   *  lib. */
  private isLocalized(): boolean {
    return (
      this.ldFlagsService.inputs.multilingualContent &&
      !!this.authService.authUser?.defaultOrgInfo?.settings
        ?.enableLocalizationContent
    );
  }

  /** Mirrors `LxpAssessContentSkillsAdapter.replaceLocalized` exactly: `update` needs the FULL
   *  content payload, so this re-sends fresh details with only the image fields replaced, then
   *  re-reads so the resolved value reflects what the server persisted. */
  private saveLocalized(
    inputId: number,
    value: AssessContentImageValue
  ): Observable<AssessContentImageValue> {
    return this.catalogApi.get<GetCatalogAssessmentResponse>(inputId).pipe(
      switchMap((details) => {
        const request = {
          ...details,
          imageUrl: value.imageUrl ?? '',
          resourceImageId: value.resourceImageId ?? undefined,
        } as unknown as CreateUpdateCatalogAssessmentRequest;
        return this.catalogApi.update<CreateUpdateCatalogAssessmentRequest>(
          request
        );
      }),
      switchMap(() =>
        this.catalogApi.get<GetCatalogAssessmentResponse>(inputId)
      ),
      map((details) => ({
        imageUrl: details.imageUrl ?? null,
        resourceImageId: normalizeResourceImageId(details.resourceImageId),
      }))
    );
  }

  /** Mirrors `LxpAssessContentSkillsAdapter.replaceLegacy` exactly: the legacy edit flow's own
   *  read/update, reused verbatim rather than inventing a parallel image-only endpoint. Re-reads
   *  afterward so the resolved value reflects exactly what the server persisted. */
  private saveLegacy(
    inputId: number,
    value: AssessContentImageValue
  ): Observable<AssessContentImageValue> {
    return this.legacyAssessmentService.getAssessment(inputId, true).pipe(
      switchMap((input) =>
        addOwnerDetailsToInput<APIResponseModel>(
          input,
          this.userGroupListService
        )
      ),
      switchMap((input) =>
        this.legacyAssessmentService.updateAssessment(
          this.buildLegacyUpdateParams(input, value)
        )
      ),
      switchMap(() =>
        this.legacyAssessmentService.getAssessment(inputId, true)
      ),
      map((res) => ({ imageUrl: res.imageUrl ?? null, resourceImageId: null }))
    );
  }

  /**
   * Builds the `/inputs/updateassessment` payload the same way
   * `LxpAssessContentSkillsAdapter.buildLegacyUpdateParams` does — seed a `ViewModel` from the
   * freshly-read assessment via `AssessmentMapper.toViewModel`, seed a plain
   * `AssessmentFormDataModel` from that view model's own fields (so every field but the image
   * round-trips unchanged), then hand both to `AssessmentMapper.buildApiParameters`.
   *
   * Unlike skills, the image fields aren't read off `formModel` by `buildApiParameters` —
   * `parseImageDataForBE(viewModel, …)` reads `viewModel.imageUrl`/`viewModel.resourceImageId`
   * directly (see that mapper's own `buildApiParameters`) — so this overrides those two fields
   * on the view model itself, after `toViewModel` has echoed in the freshly-read assessment's
   * current image, rather than on the form model like skills does with `tags`.
   */
  private buildLegacyUpdateParams(
    input: APIResponseModel & { owner: AnyRecommendee },
    value: AssessContentImageValue
  ): APIParameterModel {
    const inputContext: InputContext = {
      isEditing: true,
      isEditingInternalContent: true,
      inputId: input.inputId,
      inputType: 'Assessment',
      organizationId: this.authService.authUser?.defaultOrgId,
      renderMode: RenderMode.ContentCatalog,
    };
    const seedViewModel = {
      isEditingCredSpark: false,
      isEditingContentLocally: false,
      inputContext,
    } as ViewModel;

    const viewModel = {
      ...this.legacyAssessmentMapper.toViewModel(input, seedViewModel),
      imageUrl: value.imageUrl ?? '',
      resourceImageId: value.resourceImageId ?? undefined,
    };

    // Shaped like the catalog edit form's `form.value`, exactly like
    // `LxpAssessContentSkillsAdapter`'s own formModel — every field but the image echoes the
    // freshly-read assessment unchanged.
    const formModel: AssessmentFormDataModel = {
      credSparkToggleButton: viewModel.isCredSpark,
      assessmentUrl: viewModel.url,
      title: viewModel.title,
      provider: viewModel.userProviderName,
      description: viewModel.description,
      owner: viewModel.owner,
      skills: input.tags ?? [],
      questions: viewModel.questions,
      totalQuestions: viewModel.questions,
      questionsCorrect: undefined,
      dateCompleted: undefined,
      percentile: undefined,
      addToCatalogCheckbox: undefined,
      durationForm: {
        durationHours: viewModel.durationHours,
        durationMinutes: viewModel.durationMinutes,
      },
      advancedSettings: {
        internalItemId: viewModel.externalId,
        language: {
          id: viewModel.language ?? '',
          key: viewModel.language ?? '',
        },
        publishedDate: viewModel.publishDate,
        visibility: {
          groups: viewModel.orgContentMetadata?.groupIds ?? [],
          hideFromCatalog:
            viewModel.orgContentMetadata?.hideFromCatalog ?? false,
        },
      },
    };

    return this.legacyAssessmentMapper.buildApiParameters(
      viewModel,
      formModel,
      false
    );
  }
}

/** The catalog response types `resourceImageId` as `number | string`; the seam's own value
 *  normalizes it to `number | null`, the shape a form control holds. */
function normalizeResourceImageId(
  value: number | string | undefined
): number | null {
  if (value == null) return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}
